import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FcGoogle } from 'react-icons/fc'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

const LoginForm = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const { checkAuth } = useAuth()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        toast.error(error.message || 'Failed to login')
        setIsLoading(false)
        return
      }

      await checkAuth()
    } catch {
      toast.error('Failed to login. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      })

      if (error) {
        toast.error(error.message || 'Failed to sign in with Google')
        setIsGoogleLoading(false)
      }
    } catch {
      toast.error('Failed to sign in with Google. Please try again.')
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className='space-y-4'>
      {/* Google Sign In */}
      <Button
        type='button'
        variant='outline'
        className='w-full'
        onClick={handleGoogleSignIn}
        disabled={isLoading || isGoogleLoading}
      >
        <FcGoogle className='size-4' />
        {isGoogleLoading ? 'Redirecting...' : 'Sign in with Google'}
      </Button>

      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-card px-2 text-muted-foreground'>Or continue with</span>
        </div>
      </div>

      <form className='space-y-4' onSubmit={handleSubmit}>
        {/* Email */}
        <div className='space-y-1'>
          <Label htmlFor='userEmail' className='leading-5'>
            Email address*
          </Label>
          <Input
            type='email'
            id='userEmail'
            placeholder='Enter your email address'
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={isLoading || isGoogleLoading}
          />
        </div>

        {/* Password */}
        <div className='w-full space-y-1'>
          <Label htmlFor='password' className='leading-5'>
            Password*
          </Label>
          <div className='relative'>
            <Input
              id='password'
              type={isVisible ? 'text' : 'password'}
              placeholder='••••••••••••••••'
              className='pr-9'
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={isLoading || isGoogleLoading}
            />
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={() => setIsVisible(prevState => !prevState)}
              className='text-muted-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent'
              disabled={isLoading || isGoogleLoading}
            >
              {isVisible ? <EyeOffIcon /> : <EyeIcon />}
              <span className='sr-only'>{isVisible ? 'Hide password' : 'Show password'}</span>
            </Button>
          </div>
          <div className='flex justify-end'>
            <Link
              to='/auth/forgot-password'
              className='text-sm text-muted-foreground hover:text-foreground hover:underline'
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <Button className='w-full' type='submit' disabled={isLoading || isGoogleLoading}>
          {isLoading ? 'Signing in...' : 'Sign in to Fenra'}
        </Button>
      </form>
    </div>
  )
}

export default LoginForm
